import React from 'react';
import { isEqual } from 'lodash';
import dayjs from 'dayjs';

import {
  Box,
  FormControlLabel,
  IconButton,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimeRangePicker, TextButton } from '@components';
import { optionRowSx } from 'components/candidate/ClientFeedbackModal/components/optionRowSx';

import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';

import { CANDIDATE_INTERVIEW_CANCELLED, InterviewSlotOption } from '@constants';
import { geDateTimeInTimezone } from '@utils';
import { DateTimeRange } from '@types';

import { logEvent } from '@services';

interface IInterviewSlotsProps {
  slots: DateTimeRange[];
  selectedSlot: DateTimeRange | null;
  clientCalendar: string | null;
  candidateReadyAnytime?: boolean | null;
  clientTimezone: string;
  interviewOption: InterviewSlotOption | null;
  showCancelInterviewOption?: boolean;
  isCancelledOptionSelected?: boolean;
  error?: string;
  setInterviewOption: (newOption: InterviewSlotOption) => void;
  setSelectedSlot: (newSlot: DateTimeRange | null) => void;
  setClientCalendar: (clientCalendar: string | null) => void;
  cancelInterviewSelected?: () => void;
  deleteSelectedSlot: () => void;
}

export const InterviewSlots: React.FC<IInterviewSlotsProps> = ({
  slots,
  selectedSlot,
  clientCalendar,
  candidateReadyAnytime,
  clientTimezone,
  error,
  interviewOption,
  showCancelInterviewOption,
  isCancelledOptionSelected,
  setSelectedSlot,
  setInterviewOption,
  setClientCalendar,
  cancelInterviewSelected,
  deleteSelectedSlot,
}) => {
  const handleSlotChange = (slot: DateTimeRange) => {
    setSelectedSlot(slot);
    logEvent('interview_timeslot_selected');
  };

  return (
    <Box sx={optionRowSx}>
      <Stack gap={2}>
        {candidateReadyAnytime && (
          <Typography variant="body2">
            Candidate is available at any time. Please specify your preferred
            meeting slots.
          </Typography>
        )}
        <Stack sx={{ '&:empty': { display: 'none' } }}>
          {slots.map((slot, index) => (
            <FormControlLabel
              key={`slot_${index}`}
              value={JSON.stringify(slot)}
              onClick={() => handleSlotChange(slot)}
              control={<Radio checked={isEqual(slot, selectedSlot)} />}
              label={`${geDateTimeInTimezone(slot.start, clientTimezone).format(
                'ddd, MMM DD, hh:mm a',
              )} - ${geDateTimeInTimezone(
                slot.end ? dayjs(slot.end).toISOString() : null,
                clientTimezone,
              ).format('hh:mm a')}`}
            />
          ))}
          {!!showCancelInterviewOption && (
            <FormControlLabel
              value={CANDIDATE_INTERVIEW_CANCELLED}
              onClick={cancelInterviewSelected}
              control={<Radio checked={isCancelledOptionSelected} />}
              label={'Cancel interview'}
            />
          )}
          {interviewOption === InterviewSlotOption.MyCalendar && (
            <FormControlLabel
              control={<Radio checked={true} />}
              label={
                <TextField
                  name="my-calendly"
                  label="My calendar link"
                  placeholder="My calendar link"
                  variant="outlined"
                  fullWidth
                  value={clientCalendar || ''}
                  onChange={(e) => setClientCalendar(e.target.value || null)}
                  sx={{ background: '#FFF' }}
                />
              }
              sx={{
                '.MuiFormControlLabel-label': {
                  width: '100%',
                },
              }}
            />
          )}
          {interviewOption === InterviewSlotOption.Date && (
            <FormControlLabel
              control={<Radio checked={true} />}
              label={
                <Stack direction="row" gap="1rem">
                  <DateTimeRangePicker
                    timeZone={clientTimezone}
                    date={selectedSlot || slots?.[0]}
                    handleChange={(date) => setSelectedSlot(date)}
                    maxWidth={'20rem'}
                  />
                  <IconButton
                    onClick={() => {
                      deleteSelectedSlot();
                    }}
                  >
                    <DeleteOutlineRoundedIcon />
                  </IconButton>
                </Stack>
              }
              sx={{
                '.MuiFormControlLabel-label': {
                  width: '100%',
                },
              }}
            />
          )}
        </Stack>
        <Box display="flex" gap={4}>
          {interviewOption !== InterviewSlotOption.Date && (
            <TextButton
              variant="primary"
              onClick={() => setInterviewOption(InterviewSlotOption.Date)}
            >
              Add my slot
            </TextButton>
          )}
          {interviewOption !== InterviewSlotOption.MyCalendar && (
            <TextButton
              variant="primary"
              onClick={() => setInterviewOption(InterviewSlotOption.MyCalendar)}
            >
              Share my calendar
            </TextButton>
          )}
        </Box>
      </Stack>
      {error ? (
        <Typography
          variant="body1"
          color="text.danger"
          margin="0.5rem 0 0 1.5rem"
        >
          {error}
        </Typography>
      ) : null}
    </Box>
  );
};
